import BigNumber from 'bignumber.js';
import {
  IProvidersMap,
  ProviderManager,
  Web3KeyWriteProvider,
} from '@ankr.com/provider';

import {
  EBlockchain,
  IDepositStablecoinToPAYGForUserParams,
  IDepositStablecoinToPAYGParams,
  IGetAllowanceFeeParams,
  IGetAllowanceValueParams,
  IGetDepositStablecoinToPAYGFeeParams,
  ISetAllowanceParams,
  PrefixedHex,
  Web3Address,
} from '../../common';
import { UsdcPAYGContractManager } from '../../PAYGContract/UsdcPAYGContractManager';
import { convertNumberWithDecimalsToString } from '../../utils';

export class USDCContractService {
  constructor(
    private readonly keyProvider: Web3KeyWriteProvider,
    protected readonly usdcPAYGContractManager: UsdcPAYGContractManager,
  ) {}

  depositUSDCToPAYG({
    amount,
    depositContractAddress,
    network,
    providerManager,
    tokenAddress,
    tokenDecimals,
  }: IDepositStablecoinToPAYGParams) {
    const formattedAmount = new BigNumber(
      convertNumberWithDecimalsToString(amount, tokenDecimals),
    );

    return this.usdcPAYGContractManager.depositUSDC({
      amount: formattedAmount,
      depositContractAddress,
      network,
      providerManager,
      tokenAddress,
      tokenDecimals,
    });
  }

  getDepositUSDCToPAYGFee({
    amount,
    depositContractAddress,
    network,
    providerManager,
    tokenAddress,
    tokenDecimals,
  }: IGetDepositStablecoinToPAYGFeeParams) {
    return this.usdcPAYGContractManager.getDepositUsdcFee({
      amount,
      depositContractAddress,
      network,
      providerManager,
      tokenAddress,
      tokenDecimals,
    });
  }

  depositUSDCToPAYGForUser({
    amount,
    depositContractAddress,
    network,
    providerManager,
    targetAddress,
    tokenAddress,
    tokenDecimals,
  }: IDepositStablecoinToPAYGForUserParams) {
    const formattedAmount = new BigNumber(
      convertNumberWithDecimalsToString(amount, tokenDecimals),
    );

    return this.usdcPAYGContractManager.depositUsdcForUser({
      depositContractAddress,
      depositValue: formattedAmount,
      network,
      providerManager,
      targetAddress,
      tokenAddress,
      tokenDecimals,
    });
  }

  setAllowanceForPAYG(params: ISetAllowanceParams) {
    return this.usdcPAYGContractManager.setAllowance(params);
  }

  getAllowanceFee({
    amount,
    depositContractAddress,
    network,
    providerManager,
    tokenAddress,
    tokenDecimals,
  }: IGetAllowanceFeeParams) {
    return this.usdcPAYGContractManager.getAllowanceFee({
      amount,
      depositContractAddress,
      network,
      providerManager,
      tokenAddress,
      tokenDecimals,
    });
  }

  getTransactionReceipt(txHash: PrefixedHex) {
    return this.keyProvider.getWeb3().eth.getTransactionReceipt(txHash);
  }

  getAllowanceValue({
    depositContractAddress,
    network,
    providerManager,
    tokenAddress,
  }: IGetAllowanceValueParams) {
    return this.usdcPAYGContractManager.getAllowanceValue({
      depositContractAddress,
      network,
      providerManager,
      tokenAddress,
    });
  }

  async getLatestAllowanceEvent(user: Web3Address) {
    const events = await this.usdcPAYGContractManager.getLatestAllowanceEvents(
      user,
    );

    if (!events?.length) return undefined;

    return events[events.length - 1];
  }

  getCurrentAccountBalance(
    network: EBlockchain,
    providerManager: ProviderManager<IProvidersMap>,
  ) {
    return this.usdcPAYGContractManager.getCurrentAccountBalance(
      network,
      providerManager,
    );
  }
}
